import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as authApi from '@/api/auth';
import { authKeys } from '@/api/auth';
import { ApiException } from '@/types/api';
import type { AuthUser, LoginPayload, RegisterPayload } from '@/types/auth';

export interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** True only during the initial session check, not on later refetches. */
  isLoading: boolean;
  isVerified: boolean;

  login: (payload: LoginPayload) => Promise<AuthUser>;
  register: (payload: RegisterPayload) => Promise<AuthUser>;
  logout: () => Promise<void>;

  /** UX affordance only — the API authorizes every request independently. */
  can: (permission: string) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Session state for the application — BUILD-PLAN §17, §58.
 *
 * Authentication is cookie-based (Sanctum), so there is no token to store or
 * restore. The session is discovered by asking the API who we are, which
 * means an expired or revoked session is detected by the server rather than
 * trusted from client state.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: authKeys.user,
    queryFn: authApi.fetchAuthUser,

    /*
     | The endpoint returns null for a guest rather than 401, so the signed-out
     | case never reaches the error path at all. Retry is kept minimal for
     | genuine transport failures.
     */
    retry: (failureCount, error) => {
      if (error instanceof ApiException && error.isAuthError) {
        return false;
      }

      return failureCount < 1;
    },

    staleTime: 60_000,
  });

  const user = data ?? null;

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (authenticated) => {
      queryClient.setQueryData(authKeys.user, authenticated);
    },
  });

  const registerMutation = useMutation({
    mutationFn: authApi.register,
    onSuccess: (authenticated) => {
      queryClient.setQueryData(authKeys.user, authenticated);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      /*
       | Clear the entire cache, not just the user entry. Cart, wishlist,
       | orders and addresses are all customer-scoped, and leaving them
       | cached would show one customer's data to whoever signs in next on a
       | shared device (§66 data protection).
       */
      queryClient.clear();
      queryClient.setQueryData(authKeys.user, null);
    },
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading: isPending,
      isVerified: user?.emailVerified ?? false,

      login: (payload) => loginMutation.mutateAsync(payload),
      register: (payload) => registerMutation.mutateAsync(payload),
      logout: async () => {
        await logoutMutation.mutateAsync();
      },

      can: (permission) => user?.permissions?.includes(permission) ?? false,
    }),
    [user, isPending, loginMutation, registerMutation, logoutMutation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }

  return context;
}
